import { useState } from 'react';
import {
  Button,
  Input,
  Label,
  Modal,
  useToast,
} from '@/components/ui';
import { useCreateKnowledgeBase } from '@/hooks/useKnowledgeBases';
import type { KbScope, KnowledgeBase } from '@/types/kb';

/**
 * The scope is decided by the surface that opens the dialog — space
 * page → `scope='space'`, org admin → `scope='org'`. No toggle inside.
 * `spaceName` (when scope is space) renders a one-line destination hint
 * so the user can confirm where the KB will live.
 */
export function KbCreateDialog({
  isOpen,
  onClose,
  scope,
  spaceId,
  spaceName,
  onCreated,
}: {
  isOpen: boolean;
  onClose: () => void;
  scope: KbScope;
  spaceId?: string;
  spaceName?: string;
  onCreated?: (kb: KnowledgeBase) => void;
}) {
  const toast = useToast();
  const create = useCreateKnowledgeBase();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const reset = () => {
    setName('');
    setDescription('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      const kb = await create.mutateAsync({
        name: trimmed,
        description: description.trim() || undefined,
        scope,
        spaceId: scope === 'space' ? spaceId : undefined,
      });
      toast.success(`Knowledge base "${kb.name}" created`);
      reset();
      onClose();
      onCreated?.(kb);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create knowledge base');
    }
  };

  const destinationHint =
    scope === 'space'
      ? spaceName
        ? `Will be created in the ${spaceName}.`
        : 'Will be created in the current space.'
      : 'Will be available to every space in the organisation.';

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        reset();
        onClose();
      }}
      title="New Knowledge Base"
      size="md"
      footer={
        <>
          <Button
            variant="outline"
            onClick={() => {
              reset();
              onClose();
            }}
            disabled={create.isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} isLoading={create.isPending} disabled={!name.trim()}>
            Create
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="kb-name">Name</Label>
          <Input
            id="kb-name"
            placeholder="e.g. Onboarding handbook"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="kb-description">Description (optional)</Label>
          <Input
            id="kb-description"
            placeholder="What's in this knowledge base?"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <p className="text-xs text-muted-foreground">{destinationHint}</p>
      </form>
    </Modal>
  );
}
